import type {
  ImpersonationEnded,
  InviteLookup,
  MfaVerified,
  RecoveryCodes,
  SessionList,
  TotpEnrolStart,
} from '@fruuit/shared'
import { useQuery } from '@tanstack/react-query'
import { type ApiError, api, readCookie } from '../../api/client'
import type { MutationContext } from '../../api/hooks'

/** Auth endpoints used by the sign-in, invite and account screens (A-10). */

/**
 * Full page loads after a session switch (sign-out everywhere, View as, exit), so no
 * cached query of the previous identity survives. An object so tests can spy on it.
 */
export const browser = {
  assign(url: string): void {
    window.location.assign(url)
  },
}

export const sessionsQueryKey = ['me', 'sessions'] as const

export function useSessions() {
  return useQuery<SessionList, ApiError>({
    queryKey: sessionsQueryKey,
    queryFn: ({ signal }) => api.get<SessionList>('/me/sessions', { signal }),
  })
}

export function revokeSession(id: string, ctx: MutationContext) {
  return api.delete<{ ok: true }>(`/me/sessions/${encodeURIComponent(id)}`, {
    requestId: ctx.requestId,
  })
}

export function updateProfile(
  body: { revision: number; display_name: string },
  ctx: MutationContext,
) {
  return api.patch<{ ok: true; display_name: string; revision: number }>('/me', body, {
    requestId: ctx.requestId,
  })
}

/** Sign out on every device, then reload into sign-in (clears every cache). */
export async function signOutEverywhere(): Promise<void> {
  await api.post('/auth/signout-all', undefined, { redirectOnUnauthorized: false })
  browser.assign('/signin')
}

export function useInviteLookup(token: string) {
  return useQuery<InviteLookup, ApiError>({
    queryKey: ['invite', token],
    queryFn: ({ signal }) =>
      api.get<InviteLookup>(`/invites/${encodeURIComponent(token)}`, {
        signal,
        redirectOnUnauthorized: false,
      }),
    retry: false,
    enabled: token.length > 0,
  })
}

export function acceptInvite(token: string, ctx: MutationContext) {
  return api.post<{ ok: true; org_id: string }>(
    `/invites/${encodeURIComponent(token)}/accept`,
    undefined,
    {
      requestId: ctx.requestId,
    },
  )
}

// MFA (A-07)
export function startTotpEnrolment(_vars: undefined, ctx: MutationContext) {
  return api.post<TotpEnrolStart>('/auth/mfa/totp/enrol', undefined, { requestId: ctx.requestId })
}

export function confirmTotp(vars: { code: string }, ctx: MutationContext) {
  return api.post<RecoveryCodes>('/auth/mfa/totp/confirm', vars, { requestId: ctx.requestId })
}

export function verifyMfa(vars: { code: string }, ctx: MutationContext) {
  return api.post<MfaVerified>('/auth/mfa/verify', vars, { requestId: ctx.requestId })
}

export function disableTotp(vars: { code: string }, ctx: MutationContext) {
  return api.post<{ ok: true }>('/auth/mfa/totp/disable', vars, { requestId: ctx.requestId })
}

/** The readable marker cookie: an operator session can be restored (P-ADM-012). */
export const IMPERSONATION_MARKER = 'fe_imp'

export function canRestoreOperator(): boolean {
  return readCookie(IMPERSONATION_MARKER) === '1'
}

/**
 * Leave "View as": the API ends the 60-minute session and puts the operator's own
 * session back. A full page load then clears every cached query of the viewed user.
 */
export async function exitImpersonation(): Promise<void> {
  let redirect = '/signin'
  try {
    const res = await api.post<ImpersonationEnded>('/admin/impersonate/exit', undefined, {
      redirectOnUnauthorized: false,
    })
    redirect = res.redirect
  } finally {
    browser.assign(redirect)
  }
}
